// ══════════════════════════════════════════════════
// MÓDULO DE IMAGEN — MODELO DE DATOS
// Sphere — Gestión Hospitalaria Veterinaria
// ══════════════════════════════════════════════════

import { COLORS } from '../theme/constants';

const { primary: PRIMARY, teal: TEAL, amber: AMBER, danger: DANGER, success: SUCCESS, info: INFO, purple: PURPLE } = COLORS;

// ══════════════════════════════════════════════════
// CONFIGURACIÓN DE ESTADOS
// ══════════════════════════════════════════════════
export const IMAGING_STATUS_CONFIG = {
  pending_imaging:  { label: 'Pend. imágenes',  bg: '#FEF3C7', color: '#92400E', border: '#F59E0B' },
  imaging_done:     { label: 'Imágenes listas', bg: '#DBEAFE', color: '#1E40AF', border: '#3B82F6' },
  pending_report:   { label: 'Pend. informe',   bg: '#F3E8FF', color: '#6D28D9', border: '#A855F7' },
  draft_report:     { label: 'Borrador',         bg: '#FFF7ED', color: '#C2410C', border: '#F97316' },
  approved:         { label: 'Aprobado',         bg: '#D1FAE5', color: '#065F46', border: '#10B981' },
  reported:         { label: 'Informado',        bg: '#F1F5F9', color: '#475569', border: '#94A3B8' },
  cancelled:        { label: 'Cancelado',        bg: '#FEE2E2', color: '#991B1B', border: '#DC2626' },
};

// ══════════════════════════════════════════════════
// CONFIGURACIÓN DE PRIORIDADES
// ══════════════════════════════════════════════════
export const PRIORITY_CONFIG = {
  routine: { label: 'Rutina',  color: '#64748B', bg: '#F1F5F9' },
  urgent:  { label: 'Urgente', color: '#D97706', bg: '#FEF3C7' },
  stat:    { label: 'STAT',    color: '#DC2626', bg: '#FEE2E2', pulse: true },
};

// ══════════════════════════════════════════════════
// CONFIGURACIÓN DE MODALIDADES
// ══════════════════════════════════════════════════
export const MODALITY_CONFIG = {
  RX:         { label: 'Radiografía', icon: 'BrokenImageOutlined', color: '#1D4ED8', bg: '#EFF6FF' },
  ECO:        { label: 'Ecografía',   icon: 'WavesOutlined',       color: '#0F6E56', bg: '#E1F5EE' },
  TC:         { label: 'TC / Scanner',icon: 'RadioOutlined',       color: '#6D28D9', bg: '#F5F3FF' },
  MRI:        { label: 'RMN',         icon: 'BlurOnOutlined',      color: '#92400E', bg: '#FFF7ED' },
  DENTAL:     { label: 'Dental',      icon: 'MedicalServicesOutlined', color: '#1E40AF', bg: '#EFF6FF' },
  ENDOSCOPIA: { label: 'Endoscopía',  icon: 'CameraOutlined',      color: '#065F46', bg: '#ECFDF5' },
  OTRO:       { label: 'Otro',        icon: 'ImageOutlined',       color: '#475569', bg: '#F1F5F9' },
};

// ══════════════════════════════════════════════════
// SCHEMAS DE COLECCIONES
// ══════════════════════════════════════════════════

// imagingOrders
export const IMAGING_ORDER_SCHEMA = {
  id: 'string',
  orderNumber: 'string',
  branchId: 'string',
  branchName: 'string',
  clinicId: 'string',
  patientId: 'string',
  patientName: 'string',
  patientSpecies: 'string',
  patientBreed: 'string',
  patientWeight: 'number',
  patientAge: 'string',
  ownerId: 'string',
  ownerName: 'string',
  ownerPhone: 'string',
  ownerEmail: 'string',
  vetId: 'string',
  vetName: 'string',
  radiologistId: 'string | null',
  radiologistName: 'string | null',
  technicianId: 'string | null',
  technicianName: 'string | null',
  requestedAt: 'timestamp',
  studiesRequested: 'array',
  clinicalIndication: 'string',
  relevantHistory: 'string',
  provisionalDiagnosis: 'string',
  priority: 'routine | urgent | stat',
  orderType: 'inhouse | teleradiology',
  teleradiology: 'object | null',
  files: 'array',
  status: 'pending_imaging | imaging_done | pending_report | draft_report | approved | reported | cancelled',
  statusHistory: 'array',
  resultId: 'string | null',
  reportGeneratedAt: 'timestamp | null',
  paymentStatus: 'no_pagado | pagado',
  paymentId: 'string | null',
  createdAt: 'timestamp',
  createdBy: 'string',
  updatedAt: 'timestamp',
  hospitalizationId: 'string | null',
  labOrderId: 'string | null',
  surgeryId: 'string | null',
  sourceModule: 'string | null',
};

// imagingResults
export const IMAGING_RESULT_SCHEMA = {
  id: 'string',
  orderId: 'string',
  orderNumber: 'string',
  patientId: 'string',
  patientName: 'string',
  patientSpecies: 'string',
  radiologistId: 'string',
  radiologistName: 'string',
  findings: 'array',
  overallImpression: 'string',
  doctorComment: 'string',
  radiologistSignature: 'string',
  technicianSignature: 'string | null',
  approvedAt: 'timestamp',
  reportedAt: 'timestamp | null',
  createdAt: 'timestamp',
  updatedAt: 'timestamp',
};

// imagingExamTypes
export const IMAGING_EXAM_TYPE_SCHEMA = {
  id: 'string',
  name: 'string',
  shortName: 'string',
  category: 'string',
  modality: 'string',
  defaultViews: 'array',
  defaultBodyRegion: 'string',
  sedationTypical: 'boolean',
  contrastTypical: 'boolean',
  estimatedDurationMin: 'number',
  turnaroundTargetHours: 'number',
  species: 'array',
  arancelId: 'string | null',
  precioNeto: 'number',
  reportTemplate: 'string',
  isActive: 'boolean',
  createdAt: 'timestamp',
};

// imagingCriticalAlerts
export const IMAGING_CRITICAL_ALERT_SCHEMA = {
  id: 'string',
  orderId: 'string',
  orderNumber: 'string',
  resultId: 'string',
  patientId: 'string',
  patientName: 'string',
  vetId: 'string',
  vetName: 'string',
  criticalFindings: 'array',
  acknowledgedAt: 'timestamp | null',
  acknowledgedBy: 'string | null',
  isResolved: 'boolean',
  createdAt: 'timestamp',
};

// ══════════════════════════════════════════════════
// FUNCIONES HELPER
// ══════════════════════════════════════════════════

/**
 * Calcula el TAT (Turnaround Time) de una orden de imagen
 * @param {Object} order - Orden de imagen
 * @returns {number} Horas transcurridas desde createdAt hasta approvedAt (o hasta ahora)
 */
export const calcImagingTAT = (order) => {
  if (!order.createdAt) return 0;
  const start = order.createdAt.toDate ? order.createdAt.toDate() : new Date(order.createdAt);
  const end = order.approvedAt 
    ? (order.approvedAt.toDate ? order.approvedAt.toDate() : new Date(order.approvedAt))
    : new Date();
  return ((end - start) / (1000 * 60 * 60)).toFixed(1);
};

/**
 * Verifica si una orden está vencida según el TAT objetivo
 * @param {Object} order - Orden de imagen
 * @param {Object} examTypes - Catálogo de tipos de examen
 * @returns {boolean} true si está vencida
 */
export const isOverdue = (order, examTypes) => {
  if (order.status === 'reported' || order.status === 'cancelled') return false;
  const tat = parseFloat(calcImagingTAT(order));
  if (order.studiesRequested && order.studiesRequested.length > 0) {
    const firstStudy = order.studiesRequested[0];
    const examType = examTypes.find(e => e.id === firstStudy.examTypeId);
    if (examType && examType.turnaroundTargetHours) {
      return tat > examType.turnaroundTargetHours;
    }
  }
  return false;
};

/**
 * Genera un número de orden correlativo
 * @param {string} branchCode - Código de sucursal
 * @returns {string} Número de orden en formato IMG-{branchCode}-{YYYYMM}-{NNN}
 */
export const generateOrderNumber = (branchCode) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const random = Math.floor(Math.random() * 900) + 100;
  return `IMG-${branchCode}-${y}${m}-${random}`;
};

/**
 * Calcula la edad del paciente a partir de la fecha de nacimiento
 * @param {string|Date} birthDate - Fecha de nacimiento
 * @returns {string} Edad en formato "X años Y meses"
 */
export const calcAge = (birthDate) => {
  if (!birthDate) return '—';
  const birth = new Date(birthDate);
  const now = new Date();
  const months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  
  if (years === 0) {
    return remainingMonths === 1 ? '1 mes' : `${remainingMonths} meses`;
  }
  if (remainingMonths === 0) {
    return years === 1 ? '1 año' : `${years} años`;
  }
  return `${years} años ${remainingMonths} meses`;
};

/**
 * Obtiene la configuración de estado
 * @param {string} status - Estado de la orden
 * @returns {Object} Configuración del estado
 */
export const getStatusConfig = (status) => {
  return IMAGING_STATUS_CONFIG[status] || IMAGING_STATUS_CONFIG.pending_imaging;
};

/**
 * Obtiene la configuración de prioridad
 * @param {string} priority - Prioridad de la orden
 * @returns {Object} Configuración de la prioridad
 */
export const getPriorityConfig = (priority) => {
  return PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.routine;
};

/**
 * Obtiene la configuración de modalidad
 * @param {string} modality - Modalidad del estudio
 * @returns {Object} Configuración de la modalidad
 */
export const getModalityConfig = (modality) => {
  return MODALITY_CONFIG[modality] || MODALITY_CONFIG.OTRO;
};
